import { Box, Button, debounce, Grid } from "@mui/material";
import {
  IIssueReturnPerformanceReports,
  IssueReturnPerformanceReportsSchema,
} from "../api/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import DatePickerElement from "components/DatePickerElement";
import RadioButtonGroup from "components/RadioButtonGroup";
import AsyncSelect from "react-select/async";
import {
  IAsyncDropdownModel,
  IDropdownModel,
} from "common/models/IDropdownModel";
import requests from "common/requests/asyncRequests";
import { CloudDownloadOutlined } from "@ant-design/icons";
import api from "utils/api";
import AnimateButton from "components/@extended/AnimateButton";
import LoadingButton from "@mui/lab/LoadingButton";
import { useEffect, useRef, useState } from "react";
import MainCard from "components/MainCard";
import { SelectInstance } from "react-select";
import { SelectField } from "components/@extended/SelectField";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import { GetAdmissionYearValues } from "features/admissionYear/api/hooks";
import { ExportExcelDialog } from "components/ExportExcelDialog";
import { useGetExportFields } from "common/models/hooks/useGetExportFields";
import dayjs from "dayjs";

const IssueReturnPerformanceReports = () => {
  const groupName = "IssueReturnPerformanceReport";
  const fileName = "IssueReturnPerfomance";
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingFilterData, setPendingFilterData] = useState<any>(null);

  const { data: columns = [], isLoading: columnsLoading } =
    useGetExportFields(groupName);

  const accessionOptions: IAsyncDropdownModel[] = [];
  const handleAccessionSearch = debounce(async (query: string, callback) => {
    const options = await requests.getAccessionNo(query, null);
    callback(options.apiData || []);
  }, 500);

  const memberOptions: IAsyncDropdownModel[] = [];
  const handleMemberSearch = debounce(async (query: string, callback) => {
    const options = await requests.getMemberCode(query, null);
    callback(options.apiData || []);
  }, 500);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IIssueReturnPerformanceReports>({
    resolver: zodResolver(IssueReturnPerformanceReportsSchema),
    defaultValues: {
      filterRadioBtn: 1,
      libRadioBtn: 1,
    },
  });

  const fromDateWatch = watch("fromDate");
  const toDateWatch = watch("toDate");

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    if (data.accessionNoValue) {
      data.materialAccessionNoID = String(data?.accessionNoValue.value);
    } else {
      data.materialAccessionNoID = null;
    }
    if (data.memberCodeValue) {
      data.memberID = String(data?.memberCodeValue.value);
    } else {
      data.memberID = null;
    }

    setPendingFilterData(data);
    setDialogOpen(true);
  });

  const handleExport = (selectedFields: string[]) => {
    setLoading(true);
    const payload = {
      ...pendingFilterData,
      groupName,
      fileName,
      reportTitle: "Issue Return Top Performance",
      exportFields: selectedFields,
      parameters: pendingFilterData || {},
    };

    api.downloadExcel(
      "api/GenericExport/download",
      fileName,
      payload,
      (isLoading) => {
        setLoading(isLoading);
        if (!isLoading) {
          setDialogOpen(false);
        }
      },
    );
  };

  const { data: admissionYearQuery } = GetAdmissionYearValues();
  const [admissionYearOptions, setAdmissionYearOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setAdmissionYearOptions(admissionYearQuery?.apiData || []);
  }, [admissionYearQuery]);

  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  const instituteID = watch("instituteID");

  const { data: departmentQuery } = GetDepartmentValuesByInstitute(instituteID);
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  //#region Member Institute
  const { data: memberinstituteQuery } = GetInstituteByLibrary();
  const [memberInstituteOptions, setMemberInstituteOptions] = useState<
    IDropdownModel[]
  >([]);

  useEffect(() => {
    setMemberInstituteOptions(memberinstituteQuery?.apiData || []);
  }, [memberinstituteQuery]);

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);
  //#endregion

  //#region Member Department
  const memberInstituteID = watch("memberInstituteID");

  const { data: memberDepartmentQuery } =
    GetDepartmentValuesByInstitute(memberInstituteID);
  const [memberDepartmentOptions, setMemberDepartmentOptions] = useState<
    IDropdownModel[]
  >([]);

  useEffect(() => {
    setMemberDepartmentOptions(memberDepartmentQuery?.apiData || []);
  }, [memberDepartmentQuery]);
  //#endregion

  const handleClearFilter = () => {
    reset();
    accessionRef.current.clearValue();
    membercodeRef.current.clearValue();
  };

  const accessionRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const membercodeRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(
    null,
  );
  return (
    <>
      <MainCard
        darkTitle
        title="Issue Return Perfomance Export"
        darktitleVariant={"h4"}
      >
        <Box component={"form"} onSubmit={onSubmit}>
          <Grid container spacing={2} direction={"row"}>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <DatePickerElement
                control={control}
                name="fromDate"
                label="From Date *"
                format="DD-MM-YYYY"
                sx={{ width: "100%" }}
                maxDate={
                  toDateWatch && dayjs(toDateWatch).isValid()
                    ? dayjs(toDateWatch)
                    : undefined
                }
              ></DatePickerElement>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <DatePickerElement
                control={control}
                name="toDate"
                label="To Date *"
                format="DD-MM-YYYY"
                sx={{ width: "100%" }}
                minDate={
                  fromDateWatch && dayjs(fromDateWatch).isValid()
                    ? dayjs(fromDateWatch)
                    : undefined
                }
              ></DatePickerElement>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                options={admissionYearOptions}
                control={control}
                name="admissionYearID"
                label="Student Admission Year"
                placeholder="Select Student Admission Year"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                options={memberTypeOptions}
                control={control}
                name="memberTypeID"
                label="Member Type"
                placeholder="Select Member Type"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                options={materialTypeOptions}
                control={control}
                name="materialTypeID"
                label="Material Type"
                placeholder="Select Material Type"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                control={control}
                name="instituteID"
                options={instituteOptions}
                label="Institute"
                placeholder="Select Institute"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                control={control}
                name="departmentID"
                options={departmentOptions}
                label="Department"
                placeholder="Select Department"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                control={control}
                name="memberInstituteID"
                options={memberInstituteOptions}
                label="Member Institute"
                placeholder="Select Member Institute"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <SelectField
                control={control}
                name="memberDepartmentID"
                options={memberDepartmentOptions}
                label="Member Department"
                placeholder="Select Member Department"
              />
            </Grid>
            <Grid item xs={12} md={4.5} lg={4.5}>
              <Controller
                name="accessionNoValue"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <AsyncSelect
                    ref={accessionRef}
                    blurInputOnSelect
                    defaultValue={accessionOptions}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderColor: errors.accessionNoValue
                          ? "red"
                          : baseStyles.borderColor,
                      }),
                    }}
                    inputRef={ref}
                    getOptionLabel={(item) => item.label}
                    getOptionValue={(item) => item.value}
                    loadOptions={handleAccessionSearch}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue ? "Type Something..." : "No record found"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuShouldScrollIntoView={false}
                    placeholder="Enter Accession No. / Title / Material Code"
                    value={value}
                    isClearable
                    onChange={(selected) => onChange(selected)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4.5} lg={4.5}>
              <Controller
                name="memberCodeValue"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <AsyncSelect
                    ref={membercodeRef}
                    blurInputOnSelect
                    defaultValue={memberOptions}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),

                      control: (baseStyles) => ({
                        ...baseStyles,
                        // marginTop: 10,
                        borderColor: errors.memberCodeValue
                          ? "red"
                          : baseStyles.borderColor,
                      }),
                    }}
                    inputRef={ref}
                    getOptionLabel={(item) => item.label}
                    getOptionValue={(item) => item.value}
                    loadOptions={handleMemberSearch}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue ? "Type Something..." : "No record found"
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuShouldScrollIntoView={false}
                    placeholder="Enter Member Name / Member Code"
                    value={value}
                    isClearable
                    onChange={(selected) => onChange(selected)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={4} alignContent={"flex-end"}>
              <RadioButtonGroup
                row
                control={control}
                label="Issue Status *"
                name="filterRadioBtn"
                options={[
                  {
                    id: "1",
                    label: "All",
                  },
                  {
                    id: "2",
                    label: "Returned",
                  },
                  {
                    id: "3",
                    label: "Issued",
                  },
                  {
                    id: "4",
                    label: "Overdue",
                  },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={4} alignContent={"flex-end"}>
              <RadioButtonGroup
                row
                control={control}
                label="Library *"
                name="libRadioBtn"
                options={[
                  {
                    id: "1",
                    label: "Current Library",
                  },
                  {
                    id: "2",
                    label: "All Library",
                  },
                ]}
              />
            </Grid>

            <Grid
              container
              item
              direction={"row"}
              xs={12}
              md={12}
              lg={12}
              alignContent={"flex-end"}
            >
              <AnimateButton>
                <LoadingButton
                  disableElevation
                  disabled={isSubmitting}
                  type="submit"
                  fullWidth
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={<CloudDownloadOutlined />}
                >
                  Download
                </LoadingButton>
              </AnimateButton>
              <Button
                title="Clear"
                type="reset"
                sx={{ mx: 1 }}
                variant="contained"
                color="secondary"
                onClick={handleClearFilter}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      </MainCard>

      <ExportExcelDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        columns={columns}
        onExport={handleExport}
        loading={loading}
      />
    </>
  );
};

export default IssueReturnPerformanceReports;
